import { Check, CircleAlert, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
	removeToast,
	type ToastItem,
	type ToastType,
} from "../../../features/toast/toast.slice";
import { cn } from "../../../lib/utils";

interface ToastProps {
	toast: ToastItem;
}

const TOAST_TONE: Record<
	ToastType,
	{
		live: "polite" | "assertive";
		card: string;
		iconWrap: string;
		label: string;
		labelClass: string;
		bar: string;
	}
> = {
	success: {
		live: "polite",
		card: "border border-status-active/15 bg-status-active-bg",
		iconWrap: "bg-white/80 text-status-active",
		label: "Success",
		labelClass: "text-status-active",
		bar: "bg-status-active",
	},
	warning: {
		live: "polite",
		card: "border border-status-uninvited/20 bg-status-uninvited-bg",
		iconWrap: "bg-white/80 text-status-uninvited",
		label: "Caution",
		labelClass: "text-status-uninvited",
		bar: "bg-status-uninvited",
	},
	error: {
		live: "assertive",
		card: "border border-accent/15 bg-status-invite-expired-bg",
		iconWrap: "bg-white/80 text-accent",
		label: "Error",
		labelClass: "text-accent",
		bar: "bg-accent",
	},
};

function ToastStatusIcon({ type }: { type: ToastType }) {
	if (type === "success") {
		return <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />;
	}
	if (type === "warning") {
		return (
			<TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
		);
	}
	return <CircleAlert className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />;
}

export const Toast = ({ toast }: ToastProps) => {
	const dispatch = useDispatch();
	const duration = toast.duration ?? 4200;
	const remainingRef = useRef(duration);
	const startedAtRef = useRef<number | null>(null);
	const [paused, setPaused] = useState(false);
	const tone = TOAST_TONE[toast.type] ?? TOAST_TONE.success;

	const dismiss = useCallback(() => {
		dispatch(removeToast(toast.id));
	}, [dispatch, toast.id]);

	useEffect(() => {
		if (paused) return;
		startedAtRef.current = Date.now();
		const timer = window.setTimeout(dismiss, remainingRef.current);
		return () => {
			window.clearTimeout(timer);
			if (startedAtRef.current == null) return;
			remainingRef.current = Math.max(
				0,
				remainingRef.current - (Date.now() - startedAtRef.current),
			);
			startedAtRef.current = null;
		};
	}, [dismiss, paused]);

	return (
		<div
			role="status"
			aria-live={tone.live}
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			className={cn(
				"pointer-events-auto relative overflow-hidden rounded-2xl toast-enter",
				toast.message.includes("\n") || (toast.lines && toast.lines.length > 1)
					? "w-[min(100%,26rem)]"
					: "w-[min(100%,22rem)]",
				"ring-1 ring-black/[0.04] shadow-[0_8px_24px_rgba(15,23,42,0.08),0_2px_6px_rgba(15,23,42,0.04)]",
				tone.card,
			)}
		>
			<div className="flex items-start gap-3 px-3.5 py-3">
				<span
					className={cn(
						"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
						tone.iconWrap,
					)}
				>
					<ToastStatusIcon type={toast.type} />
				</span>
				<div className="min-w-0 flex-1 pt-0.5">
					{toast.title ? (
						<p className="text-sm font-semibold leading-5 text-primary">
							{toast.title}
						</p>
					) : (
						<p
							className={cn(
								"text-[11px] font-semibold uppercase tracking-[0.08em]",
								tone.labelClass,
							)}
						>
							{tone.label}
						</p>
					)}
					{toast.lines && toast.lines.length > 0 ? (
						<ul
							className={cn(
								"max-h-72 space-y-1 overflow-y-auto text-sm leading-5 text-primary",
								toast.title ? "mt-0.5 text-primary-muted" : "mt-0.5",
							)}
						>
							{toast.lines.map((line) => (
								<li
									key={`${line.label ?? "line"}:${line.text}`}
									className="break-words"
								>
									{line.label ? (
										<span className="font-semibold text-primary">
											{line.label}{" "}
										</span>
									) : null}
									<span>{line.text}</span>
								</li>
							))}
						</ul>
					) : (
						<p
							className={cn(
								"max-h-48 overflow-y-auto whitespace-pre-line text-sm leading-5 text-primary",
								toast.title ? "mt-0.5 text-primary-muted" : "mt-0.5",
							)}
						>
							{toast.message}
						</p>
					)}
				</div>
				<button
					type="button"
					onClick={dismiss}
					aria-label="Dismiss notification"
					className="mt-0.5 shrink-0 rounded-md p-1 text-icon transition-colors hover:bg-white/70 hover:text-primary"
				>
					<X className="h-3.5 w-3.5" aria-hidden />
				</button>
			</div>
			<div className="mx-3.5 mb-2.5 h-1 overflow-hidden rounded-full bg-black/[0.06]">
				<div
					className={cn("h-full origin-left rounded-full", tone.bar)}
					style={{
						animation: `toast-progress ${duration}ms linear forwards`,
						animationPlayState: paused ? "paused" : "running",
					}}
				/>
			</div>
		</div>
	);
};
